/**
 * Pure decision logic for terminal-outcome reporting during the recycle-kiosk
 * flow. Extracted from `App.tsx`'s two recycle-context effects so the
 * bookkeeping (when to report, when to dedupe, when to drop a stale
 * "obstructed" marker) can be unit tested without mounting the whole App.
 *
 * `App.tsx` owns:
 *  - holding the tracker state in a ref across renders
 *  - calling `decideOutcomeOnTransition` on every `recycleCtx` change
 *  - calling `decideOutcomeOnIdleReset` when `recycleCtx.state === 'idle'`
 *  - actually sending any returned `report` via `interactionReporter.report()`
 *
 * This module owns the *decision* of what to report and when — not the
 * side effect of sending it.
 */
import type { InteractionOutcomeValue } from '@dooh/shared';
import type { RecycleOverlayState } from './recycle-machine';

/**
 * Overlay screen → reported interaction outcome, for terminal screens only.
 *
 * `obstructed` is deliberately absent: it's a *recovery* state, not a
 * terminal one — the same txn can still reach `result{success}` after the
 * operator clears the jam. Reporting OBSTRUCTED on entry would race a later
 * SUCCESS for the same (screenId, txnId), and the server's unique constraint
 * is first-write-wins, silently swallowing the real outcome. Instead,
 * obstruction is reported only when it turns out to be terminal:
 * `restart_required` (below), or a reset-to-idle with no result in between
 * (see `decideOutcomeOnIdleReset`).
 */
export const OUTCOME_BY_STATE: Partial<Record<RecycleOverlayState, InteractionOutcomeValue>> = {
  product: 'SUCCESS',
  no_item_detected: 'NO_ITEM',
  no_match: 'NO_MATCH',
  barcode_not_qr: 'QR_REJECT',
  restart_required: 'OBSTRUCTED',
};

export interface ObstructedTxn {
  txnId: string;
  barcode: string | null;
}

/** Bookkeeping carried across recycle-context transitions. */
export interface OutcomeTrackerState {
  /**
   * Set when the machine enters `obstructed` for a txn; cleared once that
   * txn reaches a terminal outcome (SUCCESS/NO_ITEM/NO_MATCH/QR_REJECT/
   * restart_required's OBSTRUCTED). If it's still set when the machine
   * resets to idle, the obstruction never resolved to a result — that's a
   * terminal "obstruction recovered with no item processed", reported as
   * OBSTRUCTED exactly once (`decideOutcomeOnIdleReset`).
   */
  obstructedTxn: ObstructedTxn | null;
  /** Dedupe key (`txnId + state`) of the last outcome actually reported. */
  reportedKey: string | null;
}

export const INITIAL_OUTCOME_TRACKER_STATE: OutcomeTrackerState = {
  obstructedTxn: null,
  reportedKey: null,
};

/** A decision to report an outcome; `at` (timestamp) is stamped by the caller. */
export interface OutcomeReport {
  txnId: string;
  barcode: string | null;
  outcome: InteractionOutcomeValue;
}

export interface OutcomeDecision {
  next: OutcomeTrackerState;
  report: OutcomeReport | null;
}

/**
 * Decide whether the newest recycle-machine transition (state/txnId/barcode)
 * should produce a terminal-outcome report, and update the tracker
 * bookkeeping accordingly. Reports terminal outcomes exactly once per
 * (txnId, state) pair.
 */
export function decideOutcomeOnTransition(
  tracker: OutcomeTrackerState,
  transition: { state: RecycleOverlayState; txnId: string | null; barcode: string | null },
): OutcomeDecision {
  const { state, txnId, barcode } = transition;
  let obstructedTxn = tracker.obstructedTxn;
  let reportedKey = tracker.reportedKey;

  if (state === 'obstructed') {
    // Recovery state, not terminal — the same txn can still reach
    // `result{success}`. Track it; do NOT report yet (see OUTCOME_BY_STATE).
    if (txnId) obstructedTxn = { txnId, barcode };
    return { next: { obstructedTxn, reportedKey }, report: null };
  }

  // A new transaction started (scan) without the previous obstructed txn
  // ever passing through idle (e.g. abandoned mid-recovery) — drop the stale
  // marker so it can't misattribute a later idle-reset.
  //
  // Tradeoff (documented deliberately): this means the prior obstruction is
  // silently dropped — never reported as OBSTRUCTED — if a new scan starts
  // processing before the operator formally resolves it (e.g. by letting the
  // overlay time out to idle). We accept this because (a) a new scan
  // starting implies the jam was physically cleared, so a stale OBSTRUCTED
  // report for the abandoned txn would be a confusing, out-of-order signal
  // once the new txn's own outcome lands, and (b) the alternative — holding
  // multiple pending obstructed txns — adds real complexity for an edge case
  // with no observed operational impact.
  if (obstructedTxn && txnId && obstructedTxn.txnId !== txnId && state === 'processing') {
    obstructedTxn = null;
  }

  const outcome = OUTCOME_BY_STATE[state];
  const dedupeKey = txnId ? txnId + state : null;
  if (outcome && txnId && dedupeKey && reportedKey !== dedupeKey) {
    reportedKey = dedupeKey;
    // This txn reached a real terminal outcome — no need for the
    // idle-reset fallback to also report it.
    if (obstructedTxn?.txnId === txnId) {
      obstructedTxn = null;
    }
    return {
      next: { obstructedTxn, reportedKey },
      report: { txnId, barcode, outcome },
    };
  }

  return { next: { obstructedTxn, reportedKey }, report: null };
}

/**
 * Decide whether resetting to `idle` should flush a terminal OBSTRUCTED
 * report — i.e. the machine reset from `obstructed` (or the escalated
 * `restart_required`) without ever reaching a result. In practice this fires
 * off the back of contract v2's `recovery_complete` event (operator/agent
 * cleared the jam; `recycle-machine.ts` maps `recovery_complete` straight to
 * idle) — recovery_complete carries no `result`-style outcome of its own, so
 * the idle-reset fallback here is what actually reports OBSTRUCTED.
 */
export function decideOutcomeOnIdleReset(tracker: OutcomeTrackerState): OutcomeDecision {
  const pending = tracker.obstructedTxn;
  if (!pending) return { next: tracker, report: null };

  return {
    next: { ...tracker, obstructedTxn: null },
    report: { txnId: pending.txnId, barcode: pending.barcode, outcome: 'OBSTRUCTED' },
  };
}
